import { useMemo } from "react";
import { Box, Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useMembership from "@/shared/hooks/useMembership";
import {
  getPathPickMobileLinks,
  PATHPICK_MOBILE_SECTION_KEY,
} from "@/features/pathpick/constants/mobile-navigation";

interface EntryLink {
  // GNB와 같은 i18n 키를 재사용해 라벨이 어긋나지 않게 한다.
  key: string;
  pathname: string;
}

interface EntrySection {
  key: string;
  links: EntryLink[];
}

export default function MobileEntryPage() {
  const { t } = useTranslation();
  const { tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const identity = useIdentity();
  const { membershipStatus, isMembershipLoading } = useMembership();

  const companyType = identity?.company?.type;

  const sections = useMemo<EntrySection[]>(() => {
    // 모바일이 실제로 제공하는 화면만 나열한다.
    // 에이전시·부티크는 아직 모바일 제공 기능이 없다.
    // 오더 관리(단품오더 내역)는 모바일에서 노출하지 않는다 — 모바일 GNB와 같은 기준.
    if (companyType !== "BUYER") return [];

    // 멤버십 확정 전에는 그리지 않는다 — 로딩 중 membershipStatus가 기본값 NONE으로
    // 떨어지므로, 이 가드가 없으면 미가입 구성이 먼저 그려졌다가 바뀐다.
    if (isMembershipLoading) return [];

    // 상태별 링크 구성은 모바일 GNB와 같은 소스를 쓴다(미가입은 멤버십 한 줄).
    return [
      {
        key: PATHPICK_MOBILE_SECTION_KEY,
        links: getPathPickMobileLinks(membershipStatus),
      },
    ];
  }, [companyType, isMembershipLoading, membershipStatus]);

  return (
    <Box height={"100%"} overflowY={"auto"} padding={"24px 16px 32px"}>
      <Text fontSize={"14px"} lineHeight={"22px"} color={"#444444"}>
        {t("mobile_entry.description")}
      </Text>

      {/* 멤버십 확정 전에는 스켈레톤을 그린다 — 이 가드가 없으면 sections가 잠깐 비어
          "제공 기능 없음"이 깜빡였다가 패스픽 메뉴로 바뀐다. */}
      {isMembershipLoading ? (
        <Flex marginTop={"24px"} flexDirection={"column"} gap={"8px"}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={`MobileEntrySkeleton_${index}`}
              height={"48px"}
              borderRadius={"4px"}
            />
          ))}
        </Flex>
      ) : sections.length === 0 ? (
        <Flex
          marginTop={"24px"}
          padding={"32px 0"}
          border={"0.5px solid #D4D4D1"}
          borderRadius={"4px"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Text fontSize={"14px"} color={"#8F8F8C"}>
            {t("mobile_entry.no_features")}
          </Text>
        </Flex>
      ) : (
        <Flex marginTop={"24px"} flexDirection={"column"} gap={"20px"}>
          {sections.map((section) => (
            <Box key={section.key}>
              <Text
                fontSize={"12px"}
                fontWeight={"700"}
                color={"#8F8F8C"}
                marginBottom={"8px"}
              >
                [{tTitle(section.key)}]
              </Text>
              <Flex flexDirection={"column"} gap={"8px"}>
                {section.links.map((link) => (
                  <Button
                    key={link.key}
                    variant={"ghost"}
                    onClick={() => navigate(link.pathname)}
                    width={"100%"}
                    height={"48px"}
                    padding={"0 12px"}
                    borderRadius={"4px"}
                    border={"0.5px solid #D4D4D1"}
                    marginTop={"-0.5px"}
                    fontSize={"14px"}
                    fontWeight={"400"}
                    color={"#444444"}
                    justifyContent={"space-between"}
                  >
                    <Text as={"span"}>{tTitle(link.key)}</Text>
                    <SVG src={"/icons-v1_1/chevron-right.svg"} />
                  </Button>
                ))}
              </Flex>
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
}
